import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import MainLayout from '@/components/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Save, 
  User, 
  Copy, 
  Check, 
  Calendar, 
  MessageSquare,
  Loader2,
  AlertCircle,
  ShieldCheck,
  Clock,
  Settings2,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const SettingsPage = () => {
  const location = useLocation();
  const { user, getAuthHeaders, updateUser, checkAuth } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    qualification: user?.qualification || '',
    upi_id: user?.upi_id || '',
    upi_qr_url: user?.upi_qr_url || '',
    whatsapp_number: user?.whatsapp_number || '',
    // Phase 3: Session defaults
    timezone: user?.timezone || 'Asia/Kolkata',
    default_session_duration: user?.default_session_duration || 60,
    default_intro_duration: user?.default_intro_duration || 15
  });
  const [saving, setSaving] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState({
    connected: false,
    loading: true,
    google_email: null
  });
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  
  // WhatsApp verification state (simplified - no OTP, no QR)
  const [whatsappVerified, setWhatsappVerified] = useState(user?.whatsapp_verified || false);
  const [whatsappSaving, setWhatsappSaving] = useState(false);
  const [numberSaved, setNumberSaved] = useState(false);
  
  // Business WhatsApp number
  const BUSINESS_WHATSAPP = '7021996291';

  // Availability state
  const [availability, setAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [weeklySchedule, setWeeklySchedule] = useState({
    monday: { enabled: false, start_time: '09:00', end_time: '18:00' },
    tuesday: { enabled: false, start_time: '09:00', end_time: '18:00' },
    wednesday: { enabled: false, start_time: '09:00', end_time: '18:00' },
    thursday: { enabled: false, start_time: '09:00', end_time: '18:00' },
    friday: { enabled: false, start_time: '09:00', end_time: '18:00' },
    saturday: { enabled: false, start_time: '09:00', end_time: '18:00' },
    sunday: { enabled: false, start_time: '09:00', end_time: '18:00' }
  });
  const [slotDuration, setSlotDuration] = useState(60);

  // Check for Google Calendar callback - handle OAuth success/error
  useEffect(() => {
    // Flag to prevent multiple executions
    let hasProcessed = false;
    
    const handleGoogleCallback = async () => {
      if (hasProcessed) return;
      
      const queryParams = new URLSearchParams(location.search);
      
      // Check for new OAuth flow results
      const googleSuccess = queryParams.get('google_success');
      const googleError = queryParams.get('google_error');
      
      if (googleSuccess === 'true') {
        hasProcessed = true;
        toast.success('Google Calendar connected successfully!');
        // Refresh status once
        await fetchGoogleStatus();
        await checkAuth();
        // Clean URL immediately to prevent re-triggering
        window.history.replaceState({}, '', '/settings');
        return;
      }
      
      if (googleError) {
        hasProcessed = true;
        const errorMessages = {
          'access_denied': 'Google Calendar access was denied',
          'missing_params': 'Missing OAuth parameters',
          'invalid_state': 'Invalid or expired OAuth session',
          'expired': 'OAuth session expired. Please try again.',
          'token_exchange_failed': 'Failed to exchange authorization code',
          'no_access_token': 'No access token received from Google',
          'server_error': 'Server error during OAuth'
        };
        toast.error(errorMessages[googleError] || 'Failed to connect Google Calendar');
        // Clean URL
        window.history.replaceState({}, '', '/settings');
        return;
      }
      
      // Legacy flow handling for backward compatibility
      const hashParams = new URLSearchParams(location.hash.replace('#', ''));
      let sessionId = hashParams.get('session_id') || queryParams.get('session_id');
      
      if (!sessionId && location.hash?.includes('session_id=')) {
        sessionId = location.hash.split('session_id=')[1]?.split('&')[0]?.split('#')[0];
      }
      
      if (sessionId && user && !hasProcessed) {
        hasProcessed = true;
        setConnectingGoogle(true);
        try {
          const response = await fetch(`${API}/auth/google-calendar/connect`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({ session_id: sessionId })
          });

          const data = await response.json();

          if (response.ok && data.authorization_url) {
            // New flow returns authorization URL - redirect
            window.location.href = data.authorization_url;
            return;
          } else if (response.ok) {
            toast.success('Google Calendar connected successfully!');
            setGoogleCalendarStatus({
              connected: true,
              loading: false,
              google_email: data.google_email
            });
            await checkAuth();
          } else {
            toast.error(data.detail || 'Failed to connect Google Calendar');
          }
        } catch (error) {
          if (process.env.NODE_ENV !== 'production') console.error('Google Calendar connect error:', error);
          toast.error('Failed to connect Google Calendar: ' + error.message);
        } finally {
          setConnectingGoogle(false);
          window.history.replaceState({}, '', '/settings');
        }
      }
    };

    handleGoogleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove dependencies to run only once on mount

  // Fetch Google Calendar status
  const fetchGoogleStatus = async () => {
    try {
      const response = await fetch(`${API}/auth/google-calendar/status`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setGoogleCalendarStatus({
          connected: data.connected,
          loading: false,
          google_email: data.google_email,
          mode: data.mode,
          needs_reconnect: data.needs_reconnect
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch Google status:', error);
    } finally {
      setGoogleCalendarStatus(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchGoogleStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/therapist/profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const updated = await response.json();
      updateUser(updated);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const copyIntakeLink = () => {
    const link = `${window.location.origin}/intake/${user?.user_id}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    toast.success('Intake form link copied!');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleConnectGoogle = async () => {
    setConnectingGoogle(true);
    try {
      // Call the new OAuth init endpoint
      const response = await fetch(`${API}/auth/google/init`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.authorization_url) {
          // Redirect to Google OAuth
          window.location.href = data.authorization_url;
        } else {
          toast.error('Failed to get authorization URL');
          setConnectingGoogle(false);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to initiate Google OAuth');
        setConnectingGoogle(false);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Google OAuth init error:', error);
      toast.error('Failed to connect to Google');
      setConnectingGoogle(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      const response = await fetch(`${API}/auth/google-calendar/disconnect`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        setGoogleCalendarStatus({
          connected: false,
          loading: false,
          google_email: null
        });
        toast.success('Google Calendar disconnected');
        await checkAuth();
      }
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  // Fetch availability on mount
  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const response = await fetch(`${API}/availability`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data) {
            setAvailability(data);
            setWeeklySchedule(data.weekly_schedule || weeklySchedule);
            setSlotDuration(data.slot_duration_minutes || 60);
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch availability:', error);
      } finally {
        setAvailabilityLoading(false);
      }
    };
    
    fetchAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save availability
  const handleSaveAvailability = async () => {
    setSavingAvailability(true);
    try {
      const response = await fetch(`${API}/availability`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          weekly_schedule: weeklySchedule,
          slot_duration_minutes: slotDuration,
          buffer_minutes: 0
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save availability');
      }
      
      const data = await response.json();
      setAvailability(data);
      toast.success('Availability saved! You can now use "Slots this week" on WhatsApp.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingAvailability(false);
    }
  };

  // Update day schedule
  const handleDayChange = (day, field, value) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  // Update whatsapp verified status when user changes
  useEffect(() => {
    setWhatsappVerified(user?.whatsapp_verified || false);
    setNumberSaved(!!user?.whatsapp_number);
    setFormData(prev => ({
      ...prev,
      whatsapp_number: user?.whatsapp_number || '',
      timezone: user?.timezone || 'Asia/Kolkata',
      default_session_duration: user?.default_session_duration || 60,
      default_intro_duration: user?.default_intro_duration || 15
    }));
  }, [user?.whatsapp_verified, user?.whatsapp_number, user?.timezone, user?.default_session_duration, user?.default_intro_duration]);

  // Poll for verification status when number is saved but not verified
  useEffect(() => {
    if (!numberSaved || whatsappVerified) return;
    
    const pollInterval = setInterval(async () => {
      await checkAuth(); // This will update user state if verified
    }, 5000); // Poll every 5 seconds
    
    return () => clearInterval(pollInterval);
  }, [numberSaved, whatsappVerified, checkAuth]);

  // Save WhatsApp number with button click
  const handleSaveWhatsappNumber = async () => {
    if (!formData.whatsapp_number) {
      toast.error('Please enter your WhatsApp number');
      return;
    }
    
    // Validate format (10 digits for Indian numbers)
    const cleaned = formData.whatsapp_number.replace(/\D/g, '');
    if (cleaned.length < 10) {
      toast.error('Please enter a valid 10-digit number');
      return;
    }
    
    setWhatsappSaving(true);
    try {
      const response = await fetch(`${API}/therapist/profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ whatsapp_number: cleaned })
      });

      if (response.ok) {
        toast.success('WhatsApp number saved! Now send "Hi" to connect.');
        setNumberSaved(true);
        setFormData(prev => ({ ...prev, whatsapp_number: cleaned }));
        await checkAuth();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save WhatsApp number');
      }
    } catch (error) {
      toast.error('Failed to save WhatsApp number');
    } finally {
      setWhatsappSaving(false);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6 max-w-4xl" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 
            className="text-2xl sm:text-3xl font-semibold text-foreground"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your profile, integrations, and practice settings
          </p>
        </div>

        {/* Tabbed Navigation */}
        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-muted/50 p-1 rounded-xl">
            <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <User className="h-4 w-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="integrations" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Settings2 className="h-4 w-4 mr-2" />
              Integrations
            </TabsTrigger>
            <TabsTrigger value="availability" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Clock className="h-4 w-4 mr-2" />
              Availability
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            {/* Profile Information */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  <User className="h-5 w-5 text-primary" />
                  Profile Information
                </CardTitle>
                <CardDescription>
                  Your professional details shown to patients
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="rounded-xl"
                      data-testid="name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="rounded-xl"
                      placeholder="+91 98765 43210"
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="qualification">Qualification</Label>
                  <Textarea
                    id="qualification"
                    name="qualification"
                    value={formData.qualification}
                    onChange={handleInputChange}
                    className="rounded-xl"
                    placeholder="e.g., M.Phil Clinical Psychology, RCI Licensed"
                    data-testid="qualification-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={user?.email || ''}
                    disabled
                    className="rounded-xl bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                </div>

                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90 rounded-xl"
                  data-testid="save-profile-btn"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {saving ? 'Saving...' : 'Save Profile'}
                </Button>
              </CardContent>
            </Card>

            {/* Patient Intake Form */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  <FileText className="h-5 w-5 text-primary" />
                  Patient Intake Form
                </CardTitle>
                <CardDescription>
                  Share this link with new patients before their first session
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Input
                    value={`https://claritystack.live/intake/${user?.user_id}`}
                    readOnly
                    className="rounded-xl bg-muted font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    onClick={copyIntakeLink}
                    className="rounded-xl shrink-0"
                    data-testid="copy-intake-link-btn"
                  >
                    {copiedLink ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Patients can fill out their details, concerns, and history before meeting you.
                </p>
              </CardContent>
            </Card>

            {/* Session Defaults */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  <Calendar className="h-5 w-5 text-primary" />
                  Session Defaults
                </CardTitle>
                <CardDescription>
                  Default settings for session scheduling
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="timezone">Timezone</Label>
                    <select
                      id="timezone"
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleInputChange}
                      className="flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="timezone-select"
                    >
                      <option value="Asia/Kolkata">India (IST)</option>
                      <option value="Asia/Dubai">Dubai (GST)</option>
                      <option value="Asia/Singapore">Singapore (SGT)</option>
                      <option value="Europe/London">London (GMT/BST)</option>
                      <option value="America/New_York">New York (EST/EDT)</option>
                      <option value="America/Los_Angeles">Los Angeles (PST/PDT)</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="default_session_duration">Session Duration</Label>
                    <select
                      id="default_session_duration"
                      name="default_session_duration"
                      value={formData.default_session_duration}
                      onChange={(e) => setFormData({ ...formData, default_session_duration: parseInt(e.target.value) })}
                      className="flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="session-duration-select"
                    >
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">60 minutes</option>
                      <option value="90">90 minutes</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="default_intro_duration">Intro Call Duration</Label>
                    <select
                      id="default_intro_duration"
                      name="default_intro_duration"
                      value={formData.default_intro_duration}
                      onChange={(e) => setFormData({ ...formData, default_intro_duration: parseInt(e.target.value) })}
                      className="flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="intro-duration-select"
                    >
                      <option value="10">10 minutes</option>
                      <option value="15">15 minutes</option>
                      <option value="20">20 minutes</option>
                      <option value="30">30 minutes</option>
                    </select>
                  </div>
                </div>

                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90 rounded-xl"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Defaults
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations" className="space-y-6">
            {/* Google Calendar Integration */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  <Calendar className="h-5 w-5 text-primary" />
                  Google Calendar & Meet
                  {googleCalendarStatus.connected && googleCalendarStatus.mode === 'live' && (
                    <Badge className="ml-2 bg-green-100 text-green-700 border-0 text-xs">Live</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Connect your Google account to automatically create calendar invites and Google Meet links when sessions are paid.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {googleCalendarStatus.loading || connectingGoogle ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{connectingGoogle ? 'Connecting Google Calendar...' : 'Checking connection...'}</span>
                  </div>
                ) : googleCalendarStatus.needs_reconnect ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 border border-red-200">
                      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-red-800">Google Calendar needs reconnection</p>
                        <p className="text-sm text-red-700">Your connection has expired. Please reconnect to continue sending calendar invites.</p>
                      </div>
                    </div>
                    <Button 
                      onClick={handleConnectGoogle}
                      className="bg-primary hover:bg-primary/90 rounded-xl"
                      data-testid="reconnect-google-btn"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Reconnect Google Calendar
                    </Button>
                  </div>
                ) : googleCalendarStatus.connected ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-xl bg-green-50 border border-green-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <Check className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-green-800">Google Calendar Connected</p>
                          <p className="text-sm text-green-700">{googleCalendarStatus.google_email}</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleDisconnectGoogle}
                        className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="disconnect-google-btn"
                      >
                        Disconnect
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      When a session is marked as paid, a calendar invite with Google Meet link will be sent to the patient.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Button 
                      onClick={handleConnectGoogle}
                      className="bg-primary hover:bg-primary/90 rounded-xl"
                      data-testid="connect-google-btn"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Connect Google Calendar
                    </Button>
                    <p className="text-sm text-muted-foreground">
                      Connect your Google account to automatically send calendar invites with Google Meet links to patients when sessions are paid.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* WhatsApp Commands */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  <MessageSquare className="h-5 w-5 text-primary" />
                  WhatsApp Commands
                  {whatsappVerified ? (
                    <Badge className="ml-2 bg-green-100 text-green-700 border-0 text-xs">Connected</Badge>
                  ) : (
                    <Badge variant="outline" className="ml-2 text-xs">Not Connected</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Manage sessions directly from WhatsApp
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* WhatsApp Number Input */}
                <div className="space-y-2">
                  <Label htmlFor="whatsapp_number">Your WhatsApp Number</Label>
                  <div className="flex gap-2">
                    <Input
                      id="whatsapp_number"
                      name="whatsapp_number"
                      value={formData.whatsapp_number}
                      onChange={handleInputChange}
                      className="rounded-xl flex-1"
                      placeholder="9967124157"
                      data-testid="whatsapp-number-input"
                    />
                    {!whatsappVerified && (
                      <Button
                        onClick={handleSaveWhatsappNumber}
                        disabled={whatsappSaving || !formData.whatsapp_number}
                        className="rounded-xl bg-primary hover:bg-primary/90"
                        data-testid="save-whatsapp-btn"
                      >
                        {whatsappSaving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save
                          </>
                        )}
                      </Button>
                    )}
                    {whatsappVerified && (
                      <div className="flex items-center gap-2 px-3 bg-green-50 border border-green-200 rounded-xl">
                        <ShieldCheck className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-green-700">Connected</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enter your 10-digit WhatsApp number (without +91)
                  </p>
                </div>

                {/* Connection Instructions */}
                {numberSaved && !whatsappVerified && (
                  <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 space-y-3">
                    <div className="flex items-start gap-3">
                      <MessageSquare className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-800">Complete the connection</p>
                        <p className="text-sm text-blue-700 mt-1">
                          Send "Hi" to our WhatsApp number to verify your connection:
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-white p-3 rounded-lg border border-blue-200 text-center">
                      <p className="text-2xl font-bold text-blue-800 tracking-wider">{BUSINESS_WHATSAPP}</p>
                      <p className="text-xs text-blue-600 mt-1">Send "Hi" from your saved number</p>
                    </div>
                  </div>
                )}

                {/* WhatsApp Commands Help */}
                {whatsappVerified && (
                  <div className="p-4 rounded-xl bg-green-50 border border-green-200">
                    <p className="text-sm font-medium text-green-800 mb-2">Available Commands:</p>
                    <ul className="text-sm text-green-700 space-y-1">
                      <li>• <code className="bg-green-100 px-1 rounded">"Slots this week"</code> — View available slots</li>
                      <li>• <code className="bg-green-100 px-1 rounded">"Slots next week"</code> — View next week</li>
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Availability Tab */}
          <TabsContent value="availability" className="space-y-6">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  <Clock className="h-5 w-5 text-primary" />
                  Weekly Availability
                </CardTitle>
                <CardDescription>
                  Set your working hours for WhatsApp scheduling commands
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {availabilityLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground py-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading availability...</span>
                  </div>
                ) : (
                  <>
                    {/* Slot Duration */}
                    <div className="space-y-2">
                      <Label>Slot Duration</Label>
                      <select
                        value={slotDuration}
                        onChange={(e) => setSlotDuration(parseInt(e.target.value))}
                        className="flex h-10 w-full max-w-[200px] rounded-xl border border-input bg-background px-3 py-2 text-sm"
                        data-testid="slot-duration-select"
                      >
                        <option value="30">30 minutes</option>
                        <option value="45">45 minutes</option>
                        <option value="60">60 minutes</option>
                        <option value="90">90 minutes</option>
                      </select>
                      <p className="text-xs text-muted-foreground">
                        Default duration for each slot
                      </p>
                    </div>

                    {/* Weekly Schedule */}
                    <div className="space-y-3">
                      <Label>Weekly Schedule</Label>
                      <div className="space-y-2">
                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                          <div 
                            key={day} 
                            className={`flex items-center gap-4 p-3 rounded-xl border ${weeklySchedule[day].enabled ? 'bg-primary/5 border-primary/20' : 'bg-muted/30 border-border'}`}
                          >
                            <label className="flex items-center gap-2 cursor-pointer min-w-[120px]">
                              <input
                                type="checkbox"
                                checked={weeklySchedule[day].enabled}
                                onChange={(e) => handleDayChange(day, 'enabled', e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                data-testid={`${day}-toggle`}
                              />
                              <span className={`text-sm font-medium capitalize ${weeklySchedule[day].enabled ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {day}
                              </span>
                            </label>
                            
                            {weeklySchedule[day].enabled && (
                              <div className="flex items-center gap-2 flex-1">
                                <Input
                                  type="time"
                                  value={weeklySchedule[day].start_time}
                                  onChange={(e) => handleDayChange(day, 'start_time', e.target.value)}
                                  className="w-28 rounded-lg text-sm"
                                  data-testid={`${day}-start`}
                                />
                                <span className="text-muted-foreground text-sm">to</span>
                                <Input
                                  type="time"
                                  value={weeklySchedule[day].end_time}
                                  onChange={(e) => handleDayChange(day, 'end_time', e.target.value)}
                                  className="w-28 rounded-lg text-sm"
                                  data-testid={`${day}-end`}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Save Button */}
                    <Button
                      onClick={handleSaveAvailability}
                      disabled={savingAvailability}
                      className="bg-primary hover:bg-primary/90 rounded-xl"
                      data-testid="save-availability-btn"
                    >
                      {savingAvailability ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      {savingAvailability ? 'Saving...' : 'Save Availability'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default SettingsPage;
